import React, { useMemo, useState } from 'react';
import { NavLink, Outlet, useLocation, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../../context/AuthContext';
import UserAvatar from '../../components/UserAvatar';
import './StudentLayout.css';

const StudentLayout = () => {
  const { t } = useTranslation();
  const { user, logout } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const [navQuery, setNavQuery] = useState('');

  const handleLogout = () => {
    logout();
    navigate('/');
  };

  const studentLinks = useMemo(() => ([
    {
      path: '/student',
      label: t('portal.dashboard'),
      exact: true,
      icon: '🏠',
      section: 'overview',
      keywords: ['dashboard', 'home', 'start']
    },
    {
      path: '/student/courses',
      label: t('portal.myCourses'),
      icon: '📚',
      section: 'study',
      keywords: ['courses', 'vakken', 'modules']
    },
    {
      path: '/student/calendar',
      label: t('portal.calendar'),
      icon: '🗓️',
      section: 'study',
      keywords: ['calendar', 'agenda', 'planning']
    },
    {
      path: '/student/homework',
      label: t('nav.homework'),
      icon: '📝',
      section: 'study',
      keywords: ['homework', 'assignment', 'opdracht']
    },
    {
      path: '/student/exams',
      label: t('nav.exams'),
      icon: '🎓',
      section: 'study',
      keywords: ['exam', 'toets', 'assessment']
    },
    {
      path: '/student/grades',
      label: t('nav.grades'),
      icon: '📊',
      section: 'results',
      keywords: ['grades', 'cijfers', 'results']
    },
    {
      path: '/student/report',
      label: t('student.report'),
      icon: '📄',
      section: 'results',
      keywords: ['report', 'voortgang', 'progress']
    },
    {
      path: '/student/payments',
      label: t('nav.payments'),
      icon: '💳',
      section: 'admin',
      keywords: ['payments', 'factuur', 'financien']
    },
    {
      path: '/student/notifications',
      label: t('notification.title'),
      icon: '🔔',
      section: 'admin',
      keywords: ['notifications', 'alerts', 'meldingen']
    },
    {
      path: '/student/declarations',
      label: t('declaration.title'),
      icon: '🧾',
      section: 'admin',
      keywords: ['declarations', 'verklaring', 'documenten']
    }
  ]), [t]);

  const filteredLinks = useMemo(() => {
    const query = navQuery.trim().toLowerCase();
    if (!query) {
      return studentLinks;
    }

    return studentLinks.filter((link) => {
      const haystack = [link.label, ...(link.keywords || [])].join(' ').toLowerCase();
      return haystack.includes(query);
    });
  }, [navQuery, studentLinks]);

  const groupedLinks = useMemo(() => ({
    overview: filteredLinks.filter((link) => link.section === 'overview'),
    study: filteredLinks.filter((link) => link.section === 'study'),
    results: filteredLinks.filter((link) => link.section === 'results'),
    admin: filteredLinks.filter((link) => link.section === 'admin')
  }), [filteredLinks]);

  const currentPageLabel = useMemo(() => {
    const exact = studentLinks.find((link) => link.exact && location.pathname === link.path);
    if (exact) return exact.label;

    const fuzzy = studentLinks.find((link) => !link.exact && location.pathname.startsWith(link.path));
    return fuzzy?.label || t('portal.dashboard');
  }, [location.pathname, studentLinks, t]);

  const renderLinkGroup = (title, links) => {
    if (!links.length) return null;
    return (
      <div className="student-nav-group" key={title}>
        <p className="student-nav-group-title">{title}</p>
        {links.map((link) => (
          <NavLink
            key={link.path}
            to={link.path}
            end={link.exact}
            className={({ isActive }) => `nav-link ${isActive ? 'active' : ''}`}
          >
            <span className="nav-link-icon" aria-hidden="true">{link.icon}</span>
            <span className="nav-link-text">{link.label}</span>
          </NavLink>
        ))}
      </div>
    );
  };

  return (
    <div className="student-layout">
      <aside className="student-sidebar">
        <div className="sidebar-header">
          <UserAvatar user={user} size="lg" className="sidebar-avatar" />
          <h2>{t('student.title')}</h2>
          <p className="user-name">{user?.fullName || user?.name || ''}</p>
          <input
            type="search"
            className="student-nav-search"
            placeholder={t('common.search')}
            value={navQuery}
            onChange={(event) => setNavQuery(event.target.value)}
          />
        </div>

        <nav className="sidebar-nav">
          {renderLinkGroup(t('common.dashboard'), groupedLinks.overview)}
          {renderLinkGroup(t('portal.myCourses'), groupedLinks.study)}
          {renderLinkGroup(t('progress.title'), groupedLinks.results)}
          {renderLinkGroup(t('common.profile'), groupedLinks.admin)}
          {filteredLinks.length === 0 && (
            <p className="student-nav-empty">{t('common.noData')}</p>
          )}
        </nav>

        <div className="sidebar-footer">
          <button className="sidebar-logout-btn" onClick={handleLogout}>
            {t('nav.logout')}
          </button>
          <p>NLBA LMS</p>
        </div>
      </aside>

      <main className="student-main">
        <div className="student-main-topbar">
          <strong>{currentPageLabel}</strong>
          <div className="student-main-quicklinks">
            <NavLink to="/student" end className="student-quicklink">{t('portal.dashboard')}</NavLink>
            <NavLink to="/student/calendar" className="student-quicklink">{t('portal.calendar')}</NavLink>
            <NavLink to="/student/courses" className="student-quicklink">{t('portal.myCourses')}</NavLink>
          </div>
        </div>
        <Outlet />
      </main>
    </div>
  );
};

export default StudentLayout;
